import { useState, useEffect } from 'react';
import { updateWithdrawalStatus, siteKey } from '../../../utils/sharedStorage';
import { notifyWithdrawalValidated, notifyWithdrawalRejected } from '../../../utils/whatsappNotification';

interface Withdrawal {
  id: string;
  userPhone: string;
  userName: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  accountDetails: string;
  status: string;
  date: string;
  type?: string;
}

interface UserWithdrawalsProps {
  userPhone: string;
  userName: string;
}

export default function UserWithdrawals({ userPhone, userName }: UserWithdrawalsProps) {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    loadUserWithdrawals();
  }, [userPhone]);

  const loadUserWithdrawals = () => {
    setLoading(true);
    try {
      const raw1 = localStorage.getItem(siteKey('withdrawalRequests')) || '[]';
      const raw2 = localStorage.getItem('withdrawalRequests') || '[]';
      let all: Withdrawal[] = [];
      try {
        const a1 = JSON.parse(raw1);
        const a2 = JSON.parse(raw2);
        const combined = [...(Array.isArray(a1) ? a1 : []), ...(Array.isArray(a2) ? a2 : [])];
        const map = new Map();
        combined.forEach((w: any) => map.set(w.id, w));
        all = Array.from(map.values());
      } catch { all = []; }
      setWithdrawals(all.filter((w: any) => w.userPhone === userPhone));
    } catch {
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setErrorMsg('');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const showError = (msg: string) => {
    setErrorMsg(msg);
    setSuccessMsg('');
    setTimeout(() => setErrorMsg(''), 5000);
  };

  const handleValidate = async (w: Withdrawal) => {
    setActionLoading(w.id);
    try {
      await updateWithdrawalStatus(w.id, 'validated');
      setWithdrawals(prev => prev.map(x => x.id === w.id ? { ...x, status: 'validated' } : x));
      try {
        await notifyWithdrawalValidated(w.userPhone, w.userName || userName, w.amount, w.currency || 'FCFA', 'fr');
      } catch { /* ignore */ }
      showSuccess(`✅ Retrait de ${w.amount.toLocaleString()} F validé !`);
    } catch {
      showError('⚠️ Erreur lors de la validation.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (w: Withdrawal) => {
    setActionLoading(w.id + '_reject');
    try {
      await updateWithdrawalStatus(w.id, 'rejected');
      setWithdrawals(prev => prev.map(x => x.id === w.id ? { ...x, status: 'rejected' } : x));
      try {
        await notifyWithdrawalRejected(w.userPhone, w.userName || userName, w.amount, w.currency || 'FCFA', 'fr');
      } catch { /* ignore */ }
      showSuccess(`❌ Retrait de ${w.amount.toLocaleString()} F rejeté.`);
    } catch {
      showError('⚠️ Erreur lors du rejet.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = (id: string) => {
    try {
      const raw1 = localStorage.getItem(siteKey('withdrawalRequests')) || '[]';
      const raw2 = localStorage.getItem('withdrawalRequests') || '[]';
      let a1: any[] = []; try { a1 = JSON.parse(raw1); } catch { a1 = []; }
      let a2: any[] = []; try { a2 = JSON.parse(raw2); } catch { a2 = []; }
      localStorage.setItem(siteKey('withdrawalRequests'), JSON.stringify(a1.filter((w: any) => w.id !== id)));
      localStorage.setItem('withdrawalRequests', JSON.stringify(a2.filter((w: any) => w.id !== id)));
      setWithdrawals(prev => prev.filter(x => x.id !== id));
      showSuccess('🗑️ Retrait supprimé.');
    } catch {
      showError('⚠️ Erreur lors de la suppression.');
    }
  };

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return d; }
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'parrainage': return 'Parrainage';
      case 'taches': return 'Tâches';
      case 'points': return 'Points';
      default: return type || '';
    }
  };

  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <i className="ri-loader-4-line animate-spin text-2xl text-teal-600 mr-2"></i>
        <span className="text-gray-500 text-sm">Chargement des retraits...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">
            {withdrawals.length} retrait{withdrawals.length > 1 ? 's' : ''} au total
          </span>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full animate-pulse">
              {pendingCount} en attente
            </span>
          )}
        </div>
        <button
          onClick={loadUserWithdrawals}
          className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition cursor-pointer"
          title="Actualiser"
        >
          <i className="ri-refresh-line text-base"></i>
        </button>
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 font-semibold flex items-center gap-2">
          <i className="ri-checkbox-circle-fill"></i>{successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-semibold flex items-center gap-2">
          <i className="ri-error-warning-fill"></i>{errorMsg}
        </div>
      )}

      {withdrawals.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-xl border border-gray-200">
          <i className="ri-inbox-line text-3xl text-gray-300 block mb-2"></i>
          <p className="text-gray-400 text-sm">Aucun retrait pour cet utilisateur</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {withdrawals.map(w => (
            <div
              key={w.id}
              className={`bg-white border rounded-xl p-3 border-l-4 ${
                w.status === 'pending' ? 'border-l-yellow-400 border-gray-200' :
                w.status === 'validated' ? 'border-l-green-500 border-gray-200' :
                'border-l-red-400 border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-bold text-gray-900">
                    {w.amount.toLocaleString()} {w.currency || 'FCFA'}
                  </span>
                  {w.type && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                      {getTypeLabel(w.type)}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    w.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    w.status === 'validated' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {w.status === 'pending' ? '⏳ En attente' : w.status === 'validated' ? '✅ Validé' : '❌ Rejeté'}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(w.id)}
                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition cursor-pointer flex-shrink-0"
                  title="Supprimer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-1.5 text-xs text-gray-500 mb-2">
                <div className="flex items-center gap-1">
                  <i className="ri-bank-card-line text-gray-400"></i>
                  <span>{w.paymentMethod || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-phone-line text-gray-400"></i>
                  <span className="truncate">{w.accountDetails || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-1 col-span-2">
                  <i className="ri-calendar-line text-gray-400"></i>
                  <span>{formatDate(w.date)}</span>
                </div>
              </div>

              {/* Boutons Valider / Rejeter uniquement si en attente */}
              {w.status === 'pending' && (
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => handleReject(w)}
                    disabled={actionLoading !== null}
                    className="flex-1 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-lg text-xs transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-1 border border-red-200 disabled:opacity-50"
                  >
                    {actionLoading === w.id + '_reject' ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-close-circle-line"></i>
                    )}
                    Rejeter
                  </button>
                  <button
                    onClick={() => handleValidate(w)}
                    disabled={actionLoading !== null}
                    className="flex-1 py-1.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg text-xs transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-1 shadow disabled:opacity-50"
                  >
                    {actionLoading === w.id ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-checkbox-circle-line"></i>
                    )}
                    Valider
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
